import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface HelpCategory {
  id: string;
  title: string;
  description: string;
  icon: string;
  articles: HelpArticle[];
}

interface HelpArticle {
  id: string;
  title: string;
  description: string;
  isPopular?: boolean;
}

const HelpPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const helpCategories: HelpCategory[] = [
    {
      id: 'getting-started',
      title: t('help.categories.gettingStarted.title', 'Getting Started'),
      description: t('help.categories.gettingStarted.description', 'Learn the basics of using our platform'),
      icon: '🚀',
      articles: [
        {
          id: 'create-account',
          title: t('help.articles.createAccount.title', 'How to Create an Account'),
          description: t('help.articles.createAccount.description', 'Step-by-step guide to creating your account'),
          isPopular: true
        },
        {
          id: 'first-lawsuit',
          title: t('help.articles.firstLawsuit.title', 'Filing Your First Lawsuit'),
          description: t('help.articles.firstLawsuit.description', 'Complete guide to filing your first lawsuit')
        },
        {
          id: 'dashboard-overview',
          title: t('help.articles.dashboardOverview.title', 'Dashboard Overview'),
          description: t('help.articles.dashboardOverview.description', 'Understanding your dashboard and navigation')
        }
      ]
    },
    {
      id: 'lawsuits',
      title: t('help.categories.lawsuits.title', 'Lawsuits & Legal Actions'),
      description: t('help.categories.lawsuits.description', 'Everything about filing and managing lawsuits'),
      icon: '⚖️',
      articles: [
        {
          id: 'file-lawsuit',
          title: t('help.articles.fileLawsuit.title', 'How to File a Lawsuit'),
          description: t('help.articles.fileLawsuit.description', 'Complete process for filing a lawsuit'),
          isPopular: true
        },
        {
          id: 'join-class-action',
          title: t('help.articles.joinClassAction.title', 'Joining Class Actions'),
          description: t('help.articles.joinClassAction.description', 'How to join existing class action lawsuits')
        },
        {
          id: 'lawsuit-status',
          title: t('help.articles.lawsuitStatus.title', 'Checking Lawsuit Status'),
          description: t('help.articles.lawsuitStatus.description', 'Track the progress of your lawsuits')
        },
        {
          id: 'legal-documents',
          title: t('help.articles.legalDocuments.title', 'Legal Document Templates'),
          description: t('help.articles.legalDocuments.description', 'Using our legal document library')
        }
      ]
    },
    {
      id: 'payments',
      title: t('help.categories.payments.title', 'Payments & Billing'),
      description: t('help.categories.payments.description', 'Payment methods, billing, and subscription management'),
      icon: '💳',
      articles: [
        {
          id: 'payment-methods',
          title: t('help.articles.paymentMethods.title', 'Accepted Payment Methods'),
          description: t('help.articles.paymentMethods.description', 'All payment options we accept')
        },
        {
          id: 'subscription-management',
          title: t('help.articles.subscriptionManagement.title', 'Managing Your Subscription'),
          description: t('help.articles.subscriptionManagement.description', 'Upgrade, downgrade, or cancel your plan')
        },
        {
          id: 'billing-issues',
          title: t('help.articles.billingIssues.title', 'Resolving Billing Issues'),
          description: t('help.articles.billingIssues.description', 'Common billing problems and solutions')
        }
      ]
    },
    {
      id: 'account',
      title: t('help.categories.account.title', 'Account & Profile'),
      description: t('help.categories.account.description', 'Account settings, security, and profile management'),
      icon: '👤',
      articles: [
        {
          id: 'profile-settings',
          title: t('help.articles.profileSettings.title', 'Profile Settings'),
          description: t('help.articles.profileSettings.description', 'Update your personal information and preferences')
        },
        {
          id: 'security',
          title: t('help.articles.security.title', 'Account Security'),
          description: t('help.articles.security.description', 'Keep your account secure with best practices')
        },
        {
          id: 'data-privacy',
          title: t('help.articles.dataPrivacy.title', 'Data Privacy & GDPR'),
          description: t('help.articles.dataPrivacy.description', 'How we protect your personal information')
        }
      ]
    },
    {
      id: 'technical',
      title: t('help.categories.technical.title', 'Technical Support'),
      description: t('help.categories.technical.description', 'Technical issues and troubleshooting'),
      icon: '🔧',
      articles: [
        {
          id: 'browser-compatibility',
          title: t('help.articles.browserCompatibility.title', 'Browser Compatibility'),
          description: t('help.articles.browserCompatibility.description', 'Supported browsers and system requirements')
        },
        {
          id: 'mobile-app',
          title: t('help.articles.mobileApp.title', 'Mobile App Guide'),
          description: t('help.articles.mobileApp.description', 'Using our mobile application')
        },
        {
          id: 'api-documentation',
          title: t('help.articles.apiDocumentation.title', 'API Documentation'),
          description: t('help.articles.apiDocumentation.description', 'Developer resources and API guides')
        }
      ]
    }
  ];

  const popularArticles = helpCategories
    .flatMap(category => category.articles)
    .filter(article => article.isPopular);

  const filteredCategories = helpCategories.filter(category => {
    if (searchTerm) {
      return category.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
             category.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
             category.articles.some(article => 
               article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
               article.description.toLowerCase().includes(searchTerm.toLowerCase())
             );
    }
    return true;
  });

  const selectedCategoryData = selectedCategory 
    ? helpCategories.find(cat => cat.id === selectedCategory)
    : null;

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('help.title', 'Help Center')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('help.subtitle', 'Find answers to your questions and learn how to use our platform effectively.')}
          </p>
        </div>

        {/* Search */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('help.searchPlaceholder', 'Search for help articles...')}
              className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            />
            <svg
              className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {/* Popular Articles */}
        {!searchTerm && !selectedCategory && (
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {t('help.popularArticles', 'Popular Articles')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {popularArticles.map((article) => (
                <div key={article.id} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-cyan-100 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-cyan-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-4 flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {article.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-3">
                        {article.description}
                      </p>
                      <button className="text-cyan-600 hover:text-cyan-700 text-sm font-medium">
                        {t('help.readMore', 'Read more')} →
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Categories */}
        {!selectedCategory && (
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {t('help.browseCategories', 'Browse by Category')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCategories.map((category) => (
                <div
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow duration-200 cursor-pointer"
                >
                  <div className="flex items-center mb-4">
                    <span className="text-3xl mr-4">{category.icon}</span>
                    <h3 className="text-xl font-semibold text-gray-900">
                      {category.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    {category.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      {category.articles.length} {t('help.articles', 'articles')}
                    </span>
                    <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Category Articles */}
        {selectedCategoryData && (
          <div className="mb-16">
            <div className="flex items-center mb-6">
              <button
                onClick={() => setSelectedCategory(null)}
                className="mr-4 text-cyan-600 hover:text-cyan-700 flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                {t('help.backToCategories', 'Back to Categories')}
              </button>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm p-8">
              <div className="flex items-center mb-6">
                <span className="text-4xl mr-4">{selectedCategoryData.icon}</span>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {selectedCategoryData.title}
                  </h2>
                  <p className="text-gray-600">
                    {selectedCategoryData.description}
                  </p>
                </div>
              </div>
              
              <div className="space-y-6">
                {selectedCategoryData.articles.map((article) => (
                  <div key={article.id} className="border-b border-gray-200 pb-6 last:border-b-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {article.title}
                        </h3>
                        <p className="text-gray-600 mb-3">
                          {article.description}
                        </p>
                        <button className="text-cyan-600 hover:text-cyan-700 text-sm font-medium">
                          {t('help.readArticle', 'Read article')} →
                        </button>
                      </div>
                      {article.isPopular && (
                        <span className="ml-4 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                          {t('help.popular', 'Popular')}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Contact Support */}
        <div className="bg-gradient-to-r from-cyan-600 to-blue-600 rounded-lg p-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-4">
            {t('help.needMoreHelp', 'Need More Help?')}
          </h2>
          <p className="text-lg mb-6 opacity-90">
            {t('help.contactSupport', 'Can\'t find what you\'re looking for? Our support team is here to help.')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-cyan-600 px-6 py-3 rounded-md font-medium hover:bg-gray-100 transition-colors duration-200"
            >
              {t('help.contactUs', 'Contact Us')}
            </Link>
            <a
              href="mailto:support@tvia.ai"
              className="border-2 border-white text-white px-6 py-3 rounded-md font-medium hover:bg-white hover:text-cyan-600 transition-colors duration-200"
            >
              {t('help.emailSupport', 'Email Support')}
            </a>
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
            {t('help.quickLinks', 'Quick Links')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Link
              to="/faq"
              className="bg-white rounded-lg shadow-sm p-6 text-center hover:shadow-md transition-shadow duration-200"
            >
              <div className="text-3xl mb-4">❓</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('help.faq', 'FAQ')}
              </h3>
              <p className="text-gray-600 text-sm">
                {t('help.faqDescription', 'Frequently asked questions')}
              </p>
            </Link>
            <Link
              to="/terms"
              className="bg-white rounded-lg shadow-sm p-6 text-center hover:shadow-md transition-shadow duration-200"
            >
              <div className="text-3xl mb-4">⚖️</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('help.legalDisclaimer', 'Terms of Service')}
              </h3>
              <p className="text-gray-600 text-sm">
                {t('help.legalDisclaimerDescription', 'Important legal information')}
              </p>
            </Link>
            <Link
              to="/privacy"
              className="bg-white rounded-lg shadow-sm p-6 text-center hover:shadow-md transition-shadow duration-200"
            >
              <div className="text-3xl mb-4">🔒</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('help.privacy', 'Privacy Policy')}
              </h3>
              <p className="text-gray-600 text-sm">
                {t('help.privacyDescription', 'How we protect your data')}
              </p>
            </Link>
            <Link
              to="/terms"
              className="bg-white rounded-lg shadow-sm p-6 text-center hover:shadow-md transition-shadow duration-200"
            >
              <div className="text-3xl mb-4">📋</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('help.terms', 'Terms of Service')}
              </h3>
              <p className="text-gray-600 text-sm">
                {t('help.termsDescription', 'Platform terms and conditions')}
              </p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpPage;